import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, TrendUp, ClockCounterClockwise, CheckCircle, XCircle, Funnel, CalendarBlank, Bell, Warning, Clock, ArrowRight, UserCheck, UserMinus, X } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { StockAlertsCard } from "@/components/inventory/StockAlertsCard";

const API = `${window.location.origin}/api`;
const COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6"];

const TIME_PERIODS = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Yearly" },
  { key: "all", label: "All Time" },
];

function getDateRange(period) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  let start;
  if (period === "weekly") { start = new Date(now); start.setDate(start.getDate() - 7); }
  else if (period === "monthly") { start = new Date(now); start.setMonth(start.getMonth() - 1); }
  else if (period === "yearly") { start = new Date(now); start.setFullYear(start.getFullYear() - 1); }
  else { return { start_date: null, end_date: null }; }
  return { start_date: fmt(start), end_date: fmt(now) };
}

const formatCurrency = (amount) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(amount);

function formatFollowUpDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
}

function daysFromToday(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  return Math.round((target - today) / 86400000);
}

function getDaysLabel(days) {
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

const priorityColor = { High: "bg-red-100 text-red-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-green-100 text-green-700" };

const CHART_TICK_SM = { fontSize: 11 };
const CHART_TICK = { fontSize: 12 };
const CHART_GRID_STROKE = "#f3f4f6";
const CHART_BAR_RADIUS_TOP = [4, 4, 0, 0];
const CHART_BAR_RADIUS_RIGHT = [0, 4, 4, 0];
const CHART_MARGIN_LEFT = { left: 80 };

/* ====== Drill-Down Modal ====== */
function DrillDownModal({ open, onClose, title, color, children }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
            <span className={`w-3 h-3 rounded-full ${color}`} /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

/* ====== Follow-ups Section ====== */
function getFollowUpBorderColor(type) {
  if (type === "overdue") return "border-l-red-500";
  if (type === "today") return "border-l-orange-500";
  return "border-l-green-500";
}

function getFollowUpBgColor(type) {
  if (type === "overdue") return "bg-red-50/60";
  if (type === "today") return "bg-orange-50/60";
  return "bg-green-50/60";
}

function getFollowUpDaysColor(type) {
  if (type === "overdue") return "text-red-600";
  if (type === "today") return "text-orange-600";
  return "text-green-600";
}

function FollowUpsSection({ followups }) {
  const { overdue, today, upcoming, counts } = followups;
  const totalActive = counts.overdue + counts.today + counts.upcoming;
  const renderCard = (item, type) => {
    const days = daysFromToday(item.follow_up_date);
    const borderColor = getFollowUpBorderColor(type);
    const bgColor = getFollowUpBgColor(type);
    const daysColor = getFollowUpDaysColor(type);
    return (
      <div key={item.lead_id} className={`border-l-4 ${borderColor} ${bgColor} rounded-lg p-3`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-bold text-gray-900 text-sm truncate">{item.client_name}</p>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${priorityColor[item.priority] || priorityColor.Medium}`}>{item.priority}</span>
            </div>
            {item.company_name && <p className="text-xs text-gray-500 truncate">{item.company_name}</p>}
            {item.product_interested && <p className="text-xs text-indigo-600 truncate mt-0.5">{item.product_interested}</p>}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xs text-gray-500">{formatFollowUpDate(item.follow_up_date)}</p>
            <p className={`text-xs font-bold mt-0.5 ${daysColor}`}>{getDaysLabel(days)}</p>
            {item.assigned_to && <p className="text-[10px] text-gray-400 mt-0.5">{item.assigned_to}</p>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="followups-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
          <Bell size={18} className="text-indigo-500" weight="bold" /> Today's Follow-ups
        </h2>
        <div className="flex items-center gap-1.5 flex-wrap">
          {counts.overdue > 0 && <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid="overdue-count">{counts.overdue} Overdue</span>}
          {counts.today > 0 && <span className="text-xs font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full" data-testid="today-count">{counts.today} Today</span>}
          {counts.upcoming > 0 && <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full" data-testid="upcoming-count">{counts.upcoming} Upcoming</span>}
        </div>
      </div>
      {totalActive === 0 ? (
        <Card className="p-8 border border-gray-200 bg-white text-center" data-testid="no-followups">
          <CheckCircle size={40} className="text-green-400 mx-auto mb-2" weight="bold" />
          <p className="text-sm font-semibold text-gray-700">All caught up!</p>
          <p className="text-xs text-gray-400 mt-1">No pending follow-ups</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-4 border border-red-200 bg-white" data-testid="overdue-section">
            <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center"><Warning size={14} className="text-red-600" weight="bold" /></div><h3 className="text-sm font-bold text-red-700">Overdue ({counts.overdue})</h3></div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">{overdue.length === 0 ? <p className="text-xs text-gray-400 text-center py-4">None</p> : overdue.map((item) => renderCard(item, "overdue"))}</div>
          </Card>
          <Card className="p-4 border border-orange-200 bg-white" data-testid="today-section">
            <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center"><Clock size={14} className="text-orange-600" weight="bold" /></div><h3 className="text-sm font-bold text-orange-700">Today ({counts.today})</h3></div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">{today.length === 0 ? <p className="text-xs text-gray-400 text-center py-4">None</p> : today.map((item) => renderCard(item, "today"))}</div>
          </Card>
          <Card className="p-4 border border-green-200 bg-white" data-testid="upcoming-section">
            <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center"><ArrowRight size={14} className="text-green-600" weight="bold" /></div><h3 className="text-sm font-bold text-green-700">Upcoming ({counts.upcoming})</h3></div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">{upcoming.length === 0 ? <p className="text-xs text-gray-400 text-center py-4">None</p> : upcoming.map((item) => renderCard(item, "upcoming"))}</div>
          </Card>
        </div>
      )}
    </div>
  );
}

/* ====== Main Dashboard ====== */
export default function DashboardPage() {
  const [analytics, setAnalytics] = useState(null);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [followups, setFollowups] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [drillDown, setDrillDown] = useState(null); // { type, data }

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { start_date, end_date } = getDateRange(period);
      const params = start_date ? { start_date, end_date } : {};
      const [analyticsRes, salesRes, expensesRes, creditsRes, pendingRes, followupsRes, attRes] = await Promise.all([
        axios.get(`${API}/crm/analytics`),
        axios.get(`${API}/sales/detailed`, { params }),
        axios.get(`${API}/expenses/detailed`, { params }),
        axios.get(`${API}/credits/detailed`, { params }),
        axios.get(`${API}/pending-payments`),
        axios.get(`${API}/crm/followups`),
        axios.get(`${API}/crm/attendance-summary`),
      ]);
      setAnalytics(analyticsRes.data);
      setFollowups(followupsRes.data);
      setAttendance(attRes.data);
      setExpenseSummary({
        totalSales: salesRes.data.reduce((s, r) => s + r.amount, 0),
        salesCount: salesRes.data.length,
        totalExpenses: expensesRes.data.reduce((s, r) => s + r.amount, 0),
        expensesCount: expensesRes.data.length,
        totalCredits: creditsRes.data.reduce((s, r) => s + r.amount, 0),
        creditsCount: creditsRes.data.length,
        totalPending: pendingRes.data.reduce((s, r) => s + r.amount, 0),
        pendingCount: pendingRes.data.length,
      });
    } catch (err) { toast.error("Dashboard load failed"); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDrillDown = async (type) => {
    try {
      if (type === "won") {
        const res = await axios.get(`${API}/crm/won-leads`);
        setDrillDown({ type: "won", title: "Won Leads", color: "bg-green-500", data: res.data });
      } else if (type === "followups") {
        setDrillDown({ type: "followups", title: "Pending Follow-ups", color: "bg-amber-500", data: followups });
      } else if (type === "present") {
        setDrillDown({ type: "present", title: "Present Staff", color: "bg-green-500", data: attendance?.present_list || [] });
      } else if (type === "absent") {
        setDrillDown({ type: "absent", title: "Absent / Not Marked", color: "bg-red-500", data: attendance?.absent_list || [] });
      } else if (type === "permission") {
        setDrillDown({ type: "permission", title: "Permission Staff", color: "bg-violet-500", data: attendance?.permission_list || [] });
      } else if (type === "late") {
        setDrillDown({ type: "late", title: "Late Entries", color: "bg-orange-500", data: attendance?.late_list || [] });
      } else if (type === "total") {
        setDrillDown({ type: "total", title: "All Leads", color: "bg-indigo-500", data: analytics?.by_status || [] });
      } else if (type === "lost") {
        const res = await axios.get(`${API}/leads`, { params: { status: "Closed Lost" } });
        setDrillDown({ type: "lost", title: "Lost Leads", color: "bg-red-500", data: res.data });
      }
    } catch (err) { toast.error("Failed to load details"); }
  };

  if (loading) {
    return <div className="space-y-6">{[1, 2, 3].map((i) => <div key={`skel-${i}`} className="h-32 bg-gray-100 rounded-xl animate-pulse" />)}</div>;
  }

  const a = analytics || { total_leads: 0, closed_won: 0, closed_lost: 0, conversion_rate: 0, pending_followups: 0, by_status: [], by_source: [], by_priority: [] };
  const e = expenseSummary || { totalSales: 0, salesCount: 0, totalExpenses: 0, expensesCount: 0, totalCredits: 0, creditsCount: 0, totalPending: 0, pendingCount: 0 };
  const att = attendance || { present_count: 0, absent_count: 0, permission_count: 0, late_count: 0, total_employees: 0 };

  const crmCards = [
    { label: "Total Leads", value: a.total_leads, icon: Users, color: "text-indigo-600", bg: "bg-indigo-50", onClick: () => openDrillDown("total") },
    { label: "Won", value: a.closed_won, icon: CheckCircle, color: "text-green-600", bg: "bg-green-50", onClick: () => openDrillDown("won") },
    { label: "Lost", value: a.closed_lost, icon: XCircle, color: "text-red-600", bg: "bg-red-50", onClick: () => openDrillDown("lost") },
    { label: "Conversion", value: `${a.conversion_rate}%`, icon: TrendUp, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Follow-ups", value: a.pending_followups, icon: ClockCounterClockwise, color: "text-amber-600", bg: "bg-amber-50", onClick: () => openDrillDown("followups") },
  ];

  const attCards = [
    { label: "Present", value: att.present_count, icon: UserCheck, color: "text-green-600", bg: "bg-green-50", border: "border-green-200", onClick: () => openDrillDown("present") },
    { label: "Absent", value: att.absent_count, icon: UserMinus, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", onClick: () => openDrillDown("absent") },
    { label: "Permission", value: att.permission_count, icon: Clock, color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200", onClick: () => openDrillDown("permission") },
    { label: "Late", value: att.late_count, icon: Warning, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", onClick: () => openDrillDown("late") },
  ];

  const finCards = [
    { label: "Sales", value: formatCurrency(e.totalSales), sub: `${e.salesCount} transactions`, color: "text-green-700", labelColor: "text-green-700", border: "border-green-200", bg: "bg-green-50", onClick: () => window.location.href = "/expenses" },
    { label: "Expenses", value: formatCurrency(e.totalExpenses), sub: `${e.expensesCount} transactions`, color: "text-red-700", labelColor: "text-red-700", border: "border-red-200", bg: "bg-red-50", onClick: () => window.location.href = "/expenses" },
    { label: "Credits", value: formatCurrency(e.totalCredits), sub: `${e.creditsCount} entries`, color: "text-blue-700", labelColor: "text-blue-700", border: "border-blue-200", bg: "bg-blue-50", onClick: () => window.location.href = "/expenses" },
    { label: "Pending", value: formatCurrency(e.totalPending), sub: `${e.pendingCount} payments`, color: "text-amber-700", labelColor: "text-amber-700", border: "border-amber-200", bg: "bg-amber-50", onClick: () => window.location.href = "/expenses" },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 max-w-7xl" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Dashboard</h1>
          <p className="text-xs sm:text-sm font-bold text-indigo-600 mt-1">Tap any card for full details</p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto mobile-scroll-x" data-testid="period-filter">
          {TIME_PERIODS.map((tp) => (
            <Button key={tp.key} size="sm" variant={period === tp.key ? "default" : "ghost"}
              className={`h-8 text-xs ${period === tp.key ? "bg-indigo-600 text-white" : "text-gray-600 hover:text-gray-900"}`}
              onClick={() => setPeriod(tp.key)} data-testid={`period-${tp.key}`}
            >{tp.label}</Button>
          ))}
        </div>
      </div>

      {/* CRM Stats - Clickable */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {crmCards.map((s) => (
          <Card key={s.label} className={`p-3 sm:p-4 border-2 border-gray-200 bg-white ${s.onClick ? "cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:border-indigo-400 transition-all" : ""}`} onClick={s.onClick} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}><s.icon size={18} className={s.color} weight="bold" /></div>
              <div className="min-w-0"><p className={`text-[11px] sm:text-xs font-bold uppercase tracking-wide truncate ${s.color}`}>{s.label}</p><p className="text-xl sm:text-2xl font-black text-gray-900 truncate" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.value}</p></div>
            </div>
            {s.onClick && <p className={`text-[10px] mt-1 font-semibold ${s.color}`}>VIEW →</p>}
          </Card>
        ))}
      </div>

      {/* Attendance Row - Clickable */}
      <div>
        <h2 className="text-base sm:text-lg font-black text-gray-900 mb-2 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
          <span className="w-1 h-5 bg-green-600 rounded-full"></span>
          <UserCheck size={18} className="text-green-600" weight="bold" /> Today's Attendance ({att.total_employees} staff)
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          {attCards.map((c) => (
            <Card key={c.label} className={`p-3 border-2 ${c.border} bg-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all`} onClick={c.onClick} data-testid={`att-stat-${c.label.toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-lg ${c.bg} flex items-center justify-center`}><c.icon size={16} className={c.color} weight="bold" /></div>
                <div><p className="text-2xl font-black text-gray-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.value}</p><p className={`text-[10px] font-bold uppercase tracking-wide ${c.color}`}>{c.label}</p></div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Today's Follow-ups */}
      {followups && <FollowUpsSection followups={followups} />}

      <StockAlertsCard />

      {/* Financial Summary */}
      <div>
        <h2 className="text-base sm:text-lg font-black text-gray-900 mb-3 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
          <span className="w-1 h-5 bg-indigo-600 rounded-full"></span>
          <CalendarBlank size={18} className="text-indigo-600" weight="bold" /> Financial Summary ({TIME_PERIODS.find((t) => t.key === period)?.label})
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {finCards.map((item) => (
            <Card key={item.label} className={`p-3 sm:p-4 border-2 ${item.border} ${item.bg} cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all`} onClick={item.onClick} data-testid={`fin-${item.label.toLowerCase()}`}>
              <p className={`text-[11px] sm:text-xs font-bold uppercase tracking-wide mb-1 ${item.labelColor}`}>{item.label}</p>
              <p className={`text-base sm:text-2xl font-black ${item.color}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{item.value}</p>
              <p className={`text-[10px] sm:text-xs font-semibold mt-1 ${item.labelColor}`}>{item.sub} →</p>
            </Card>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 border border-gray-200 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2"><Funnel size={16} className="text-indigo-500" /> Lead Pipeline</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={a.by_status} layout="vertical" margin={CHART_MARGIN_LEFT}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} /><XAxis type="number" tick={CHART_TICK} /><YAxis dataKey="name" type="category" tick={CHART_TICK_SM} width={80} /><Tooltip /><Bar dataKey="count" fill="#6366f1" radius={CHART_BAR_RADIUS_RIGHT} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5 border border-gray-200 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Lead Sources</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={a.by_source} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, count }) => `${name}: ${count}`}>
                {a.by_source.map((entry) => <Cell key={entry.name} fill={COLORS[a.by_source.indexOf(entry) % COLORS.length]} />)}
              </Pie><Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* ====== DRILL-DOWN MODAL ====== */}
      <DrillDownModal open={drillDown !== null} onClose={() => setDrillDown(null)} title={drillDown?.title || ""} color={drillDown?.color || "bg-gray-400"}>
        {/* Won Leads */}
        {drillDown?.type === "won" && (
          <div className="space-y-2">
            {drillDown.data.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No won leads yet</p> : drillDown.data.map((l) => (
              <div key={l.lead_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200" data-testid={`drill-won-${l.lead_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-gray-900">{l.client_name}</p>
                  {l.company_name && <p className="text-xs text-gray-500">{l.company_name}</p>}
                  {l.product_interested && <p className="text-xs text-indigo-600">{l.product_interested}</p>}
                </div>
                {l.assigned_to && <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full border">{l.assigned_to}</span>}
              </div>
            ))}
          </div>
        )}
        {/* Lost Leads */}
        {drillDown?.type === "lost" && (
          <div className="space-y-2">
            {drillDown.data.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No lost leads</p> : drillDown.data.map((l) => (
              <div key={l.lead_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200" data-testid={`drill-lost-${l.lead_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-gray-900">{l.client_name}</p>
                  {l.company_name && <p className="text-xs text-gray-500">{l.company_name}</p>}
                </div>
                {l.assigned_to && <span className="text-xs text-gray-500">{l.assigned_to}</span>}
              </div>
            ))}
          </div>
        )}
        {/* Present Staff */}
        {drillDown?.type === "present" && (
          <div className="space-y-2">
            {drillDown.data.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No one present yet</p> : drillDown.data.map((p, i) => (
              <div key={`present-${i}`} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200" data-testid={`drill-present-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center text-xs font-bold text-green-700">{p.name?.charAt(0)}</div>
                  <p className="font-bold text-sm text-gray-900">{p.name}</p>
                </div>
                <span className="text-xs text-green-600 font-mono font-bold">{p.check_in || "-"}</span>
              </div>
            ))}
          </div>
        )}
        {/* Absent Staff */}
        {drillDown?.type === "absent" && (
          <div className="space-y-2">
            {drillDown.data.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">Everyone present!</p> : drillDown.data.map((p, i) => (
              <div key={`absent-${i}`} className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200" data-testid={`drill-absent-${i}`}>
                <div className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center text-xs font-bold text-red-700">{p.name?.charAt(0)}</div>
                <p className="font-bold text-sm text-gray-900">{p.name}</p>
              </div>
            ))}
          </div>
        )}
        {/* Permission Staff */}
        {drillDown?.type === "permission" && (
          <div className="space-y-2">
            {drillDown.data.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No permissions today</p> : drillDown.data.map((p, i) => (
              <div key={`perm-${i}`} className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-200" data-testid={`drill-perm-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-violet-200 flex items-center justify-center text-xs font-bold text-violet-700">{p.name?.charAt(0)}</div>
                  <div><p className="font-bold text-sm text-gray-900">{p.name}</p>{p.reason && <p className="text-xs text-violet-500">{p.reason}</p>}</div>
                </div>
                <span className="text-sm text-violet-700 font-bold font-mono">{p.hours}h</span>
              </div>
            ))}
          </div>
        )}
        {/* Late */}
        {drillDown?.type === "late" && (
          <div className="space-y-2">
            {drillDown.data.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No late entries</p> : drillDown.data.map((p, i) => (
              <div key={`late-${i}`} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200" data-testid={`drill-late-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-orange-200 flex items-center justify-center text-xs font-bold text-orange-700">{p.name?.charAt(0)}</div>
                  <p className="font-bold text-sm text-gray-900">{p.name}</p>
                </div>
                <span className="text-xs text-orange-600 font-mono font-bold">In: {p.check_in}</span>
              </div>
            ))}
          </div>
        )}
        {/* Follow-ups Drill-Down */}
        {drillDown?.type === "followups" && drillDown.data && (
          <div className="space-y-3">
            {[...(drillDown.data.overdue || []), ...(drillDown.data.today || []), ...(drillDown.data.upcoming || [])].map((item) => {
              const days = daysFromToday(item.follow_up_date);
              const isOverdue = days < 0;
              return (
                <div key={item.lead_id} className={`p-3 rounded-lg border ${isOverdue ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`} data-testid={`drill-fu-${item.lead_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-sm text-gray-900">{item.client_name}</p>
                      <p className="text-xs text-gray-500">{item.company_name || ""} {item.product_interested ? `- ${item.product_interested}` : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs font-bold ${isOverdue ? "text-red-600" : "text-amber-600"}`}>{getDaysLabel(days)}</p>
                      <p className="text-[10px] text-gray-400">{formatFollowUpDate(item.follow_up_date)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Total Leads by Status */}
        {drillDown?.type === "total" && (
          <div className="space-y-2">
            {drillDown.data.map((s) => (
              <div key={s.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="font-semibold text-sm text-gray-900">{s.name}</p>
                <span className="text-lg font-bold text-indigo-600 font-mono">{s.count}</span>
              </div>
            ))}
          </div>
        )}
      </DrillDownModal>
    </div>
  );
}
